import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listPayments, downloadPaymentsExport, type Payment } from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";
import { useAuth } from "@/contexts/AuthContext";
import { Download } from "lucide-react";

export function PaymentsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  // Moko staff = user cross-tenant (pas de merchant_id). Couvre admin, support,
  // finance-moko (rôle à venir migration 010) — le discriminant est la scope,
  // pas le rôle (car `finance` existe aussi côté tenant marchand).
  const isMokoStaff = auth.status === "authenticated" && !auth.user.merchant_id;
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadPaymentsExport({
        status: status || undefined,
        payment_method: method || undefined,
        q: search.trim() || undefined,
      });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erreur d'export");
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const resp = await listPayments({
        limit: 100,
        status: status || undefined,
        payment_method: method || undefined,
        q: search.trim() || undefined,
      });
      setPayments(resp.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, method]);

  const columns: ColumnDef<Payment>[] = [
    // Colonnes Marchand + Code : visibles UNIQUEMENT pour l'équipe Moko
    // (users cross-tenant, sans merchant_id). Un user marchand ne voit
    // évidemment que sa propre data, donc ces colonnes n'apporteraient rien.
    ...(isMokoStaff
      ? ([
          {
            key: "merchant_name",
            header: "Marchand",
            minWidth: 180,
            cell: (r) => (
              <span className="text-sm text-[#181c32]">{r.merchant_business_name ?? "—"}</span>
            ),
          },
          {
            key: "merchant_code",
            header: "Code marchand",
            minWidth: 160,
            cell: (r) => (
              <span className="font-mono text-xs text-[#7e8299]">{r.merchant_code ?? "—"}</span>
            ),
          },
        ] as ColumnDef<Payment>[])
      : []),
    {
      key: "paydrc_ref",
      header: "Réf. PayDRC",
      minWidth: 200,
      accessor: (r) => r.paydrc_reference ?? "",
      sortable: true,
      wrap: true,
      cell: (r) => (
        <span className="font-mono text-xs text-[#3f4254]" title="Référence Moko/PayDRC">
          {r.paydrc_reference ?? "—"}
        </span>
      ),
    },
    {
      key: "thirdparty_ref",
      header: "Réf. Marchand",
      minWidth: 200,
      accessor: (r) => r.thirdparty_reference ?? "",
      sortable: true,
      wrap: true,
      cell: (r) => (
        <span className="font-mono text-xs text-[#181c32] font-semibold" title="Référence fournie par le marchand à l'init de la transaction">
          {r.thirdparty_reference ?? "—"}
        </span>
      ),
    },
    {
      key: "telco_ref",
      header: "Réf. Telco",
      minWidth: 180,
      accessor: (r) => r.telco_reference ?? "",
      sortable: true,
      wrap: true,
      cell: (r) => (
        <span className="font-mono text-xs text-[#7e8299]" title="Référence de confirmation telco (M-Pesa, Orange, Airtel…)">
          {r.telco_reference ?? "—"}
        </span>
      ),
    },
    {
      key: "client_paid",
      header: "Client a payé",
      align: "right",
      minWidth: 120,
      sortable: true,
      accessor: (r) => r.client_paid_minor ?? r.amount_minor,
      cell: (r) => {
        const paid = r.client_paid_minor ?? r.amount_minor;
        const gonfle = r.fee_bearer === "CUSTOMER" && paid > r.amount_minor;
        return (
          <span
            className="font-semibold text-[#181c32]"
            title={gonfle
              ? `Le client a été débité de ce montant (STK gonflé de ${((paid - r.amount_minor) / r.amount_minor * 100).toFixed(1)} % pour couvrir les frais)`
              : "Ce que le client a payé sur son STK"}
          >
            {formatAmount(paid, r.currency).replace(` ${r.currency}`, "")}
          </span>
        );
      },
    },
    {
      key: "amount_merchant",
      header: "Vous avez encaissé",
      align: "right",
      minWidth: 130,
      sortable: true,
      accessor: (r) => r.amount_merchant_minor ?? 0,
      cell: (r) => {
        const net = r.amount_merchant_minor ?? 0;
        return (
          <span
            className="font-semibold text-success-700"
            title="Net crédité sur votre wallet (après commissions Moko + telco)"
          >
            {net > 0 ? formatAmount(net, r.currency).replace(` ${r.currency}`, "") : "—"}
          </span>
        );
      },
    },
    {
      key: "fees",
      header: "Frais",
      align: "right",
      minWidth: 100,
      sortable: true,
      accessor: (r) => r.fees_minor ?? r.commission_minor ?? 0,
      cell: (r) => {
        const fees = r.fees_minor ?? r.commission_minor ?? 0;
        if (fees <= 0) return <span className="text-[#7e8299]">—</span>;
        const moko = r.moko_fee_minor ?? 0;
        const telcoMoko = r.telco_fee_moko_wallet_minor ?? 0;
        const telcoCust = r.telco_fee_customer_paid_minor ?? 0;
        const tip = r.has_snapshot
          ? `Ventilation : Moko ${formatAmount(moko, r.currency).replace(` ${r.currency}`, "")} · Telco/Moko ${formatAmount(telcoMoko, r.currency).replace(` ${r.currency}`, "")}${telcoCust > 0 ? ` · Telco/Client ${formatAmount(telcoCust, r.currency).replace(` ${r.currency}`, "")}` : ""}`
          : "Frais totaux (ventilation détaillée disponible après bascule snapshot)";
        return (
          <span className="font-medium text-[#3f4254]" title={tip}>
            {formatAmount(fees, r.currency).replace(` ${r.currency}`, "")}
          </span>
        );
      },
    },
    {
      key: "fee_bearer",
      header: "Qui paie",
      minWidth: 130,
      sortable: true,
      accessor: (r) => r.fee_bearer ?? "MERCHANT",
      cell: (r) => {
        const bearer = r.fee_bearer ?? "MERCHANT";
        if (bearer === "CUSTOMER") {
          return (
            <span
              className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-sky-100 text-sky-800 border border-sky-200"
              title="Le client a payé les frais en plus (STK gonflé)"
            >
              Client
            </span>
          );
        }
        return (
          <span
            className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-700 border border-slate-200"
            title="Vous absorbez les frais (déduits du montant reçu)"
          >
            Vous
          </span>
        );
      },
    },
    {
      key: "currency",
      header: "Devise",
      minWidth: 80,
      sortable: true,
      accessor: (r) => r.currency,
      cell: (r) => <span className="text-sm font-semibold text-[#3f4254]">{r.currency}</span>,
    },
    {
      key: "method",
      header: "Méthode",
      minWidth: 110,
      sortable: true,
      accessor: (r) => r.payment_method,
      cell: (r) => <Badge color={methodColor(r.payment_method)}>{r.payment_method}</Badge>,
    },
    {
      key: "env",
      header: "Env.",
      minWidth: 80,
      sortable: true,
      accessor: (r) => (r.livemode ? "live" : "test"),
      cell: (r) =>
        r.livemode ? (
          <span
            title="Transaction en environnement réel (débit bancaire effectif)"
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200"
          >
            Réel
          </span>
        ) : (
          <span
            title="Transaction en environnement test (carte virtuelle, aucun débit réel)"
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200"
          >
            Test
          </span>
        ),
    },
    {
      key: "customer",
      header: "Client",
      minWidth: 180,
      wrap: true,
      cell: (r) => (
        <div>
          <div className="text-sm font-medium text-[#181c32]">
            {[r.customer_first_name, r.customer_last_name].filter(Boolean).join(" ") || "—"}
          </div>
          <div className="text-xs text-[#7e8299]">{r.customer_phone || r.customer_email || "—"}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 110,
      sortable: true,
      accessor: (r) => r.status,
      cell: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: "description",
      header: "Description",
      minWidth: 240,
      wrap: true,
      cell: (r) => {
        if (!r.failure_message) return <span className="text-xs text-[#a1a5b7]">—</span>;
        const color = r.status === "succeeded" ? "text-success-700" : "text-danger-700";
        return (
          <span className={`text-xs ${color} block max-w-[280px]`} title={r.failure_message}>
            {r.failure_message}
          </span>
        );
      },
    },
    {
      key: "created_at",
      header: "Date",
      minWidth: 130,
      sortable: true,
      accessor: (r) => r.created_at,
      cell: (r) => <span className="text-xs text-[#7e8299]">{formatDate(r.created_at)}</span>,
    },
  ];

  const filters = (
    <>
      <div className="relative">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Réf. PayDRC / Marchand / Telco, client…"
          className="form-input py-2 px-3 text-sm w-64"
        />
      </div>
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Tous statuts</option>
        <option value="succeeded">Succeeded</option>
        <option value="processing">Processing</option>
        <option value="pending">Pending</option>
        <option value="failed">Failed</option>
      </select>
      <select value={method} onChange={(e) => setMethod(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Toutes méthodes</option>
        <option value="mpesa">M-Pesa</option>
        <option value="orange">Orange</option>
        <option value="airtel">Airtel</option>
        <option value="afrimoney">AfriMoney</option>
        <option value="visa">Visa</option>
        <option value="mastercard">Mastercard</option>
      </select>
      <button onClick={load} className="btn btn-primary btn-sm">Filtrer</button>
    </>
  );

  return (
    <div>
      <PageHeader
        title="Paiements"
        breadcrumbs={[{ label: "Transactions" }, { label: "Paiements" }]}
        actions={
          <button
            type="button"
            onClick={onExport}
            disabled={exporting}
            className="btn btn-light btn-sm"
            title="Exporter les paiements filtrés en CSV"
          >
            <Download size={14} />
            {exporting ? "Export…" : "Exporter CSV"}
          </button>
        }
      />

      <DataTable<Payment>
        title="Transactions"
        subtitle="Recherche par référence, statut, ou méthode"
        searchable
        searchPlaceholder="Rechercher dans les résultats…"
        columns={columns}
        data={payments}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/payments/${r.id}`)}
        loading={loading}
        filtersSlot={filters}
        emptyMessage="Aucun paiement trouvé"
      />
    </div>
  );
}

function methodColor(method: string): "primary" | "info" | "warning" | "light" {
  if (method === "mpesa" || method === "afrimoney") return "primary";
  if (method === "orange") return "warning";
  if (method === "airtel") return "info";
  return "light";
}
